'use client';

import { useMemo } from 'react';
import { ArrowLeft, Heart, Share2 } from 'lucide-react';
import { cn } from '@/shadcn/lib/utils';
import { Img } from '@/shared/components/ui/Img';
import { Separator } from '@/shadcn/components/ui/separator';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/shadcn/components/ui/accordion';
import { LocationOutlinedIcon } from '@/shared/components/icons';
import { Listing } from '../types';
import {
    formatListingPrice,
    formatListingPricePerM2,
    mapPropertySubTypeToText,
    resolveListingSubtype,
} from '../utils';
import { resolveListingCategoryIcon } from '../utils/categoryIcon';
import {
    getListingMainInfo,
    getListingOutdoorSpaceItems,
    getListingParkingItems,
} from '../utils/listingDetail';
import { getListingAttributeSections } from '../utils/listingAttributes';
import { ListingMedia } from './ListingMedia';
import { ListingGalleryThumbs } from './ListingGalleryThumbs';
import { ListingDetailSection } from './ListingDetailSection';
import { ListingCollapse } from './ListingCollapse';
import { ListingContactForm } from './ListingContactForm';
import { SearchListingCard } from './SearchListingCard';

const EASE = '[transition-timing-function:cubic-bezier(.22,.61,.36,1)]';

type Props = {
    listing: Listing;
    /** ostatne ponuky vo vysledkoch - pre "Podobne ponuky" */
    others: Listing[];
    onClose: () => void;
    onOpen: (listing: Listing) => void;
};

/**
 * Detail ponuky v search mode - lavy stlpec split view (2/3), mapa vpravo (1/3).
 *
 * Obsahovo 1:1 to, co mame v /inzerat/[id]/[slug] (media, hlavne info, parkovanie,
 * vonkajsie priestory, atributy, popis, kontakt), poskladane z tych istych klientskych
 * komponentov - ziadny novy vizual sekcii, len obal v karte ako maju vysledky.
 * Mapa a "Podobne inzeraty" slider z povodnej stranky tu nie su: mapa je vedla
 * (ta ista instancia), podobne ponuky su karty z vysledkov.
 */
export function ListingDetailPanel({ listing, others, onClose, onOpen }: Props) {
    const CategoryIcon = resolveListingCategoryIcon(listing.propertyCategory);
    const subtype = resolveListingSubtype(listing);
    const subtypeText = subtype ? mapPropertySubTypeToText(subtype) : null;

    const mainInfo = useMemo(() => getListingMainInfo(listing), [listing]);
    const parking = useMemo(() => getListingParkingItems(listing), [listing]);
    const outdoor = useMemo(() => getListingOutdoorSpaceItems(listing), [listing]);
    const attributeSections = useMemo(() => getListingAttributeSections(listing), [listing]);

    // rovnaka logika ako na /inzerat: prve 3 fotky ma media blok, zvysok su thumby
    const thumbs = useMemo(
        () => (listing.gallery ?? []).map((g) => ({ src: g.url })).slice(3),
        [listing.gallery],
    );

    const similar = useMemo(() => {
        const same = others.filter((o) => o.propertyCategory === listing.propertyCategory);
        return (same.length >= 2 ? same : others).slice(0, 2);
    }, [others, listing.propertyCategory]);

    const pricePerM2 = formatListingPricePerM2(listing);
    const title = (listing as { slogan?: string }).slogan ?? '';

    return (
        <article className="rx-detail">
            {/* media + thumby: biela karta, vnutorne rounded fotky ostavaju z ListingMedia */}
            <div className="overflow-hidden rounded-[18px] bg-white border border-black/[0.04] shadow-[0_1px_2px_rgba(0,0,0,0.05),0_8px_22px_-12px_rgba(0,0,0,0.16)]">
                <ListingMedia listing={listing} />
                {thumbs.length > 0 && (
                    <div className="p-[8px] pt-[6px] xl:p-[10px] xl:pt-[8px]">
                        <ListingGalleryThumbs
                            listing={{ id: listing.id, slug: listing.slug }}
                            gallery={thumbs}
                        />
                    </div>
                )}
            </div>

            {/* obsah */}
            <div className="mt-[16px] rounded-[18px] bg-white border border-black/[0.04] shadow-[0_1px_2px_rgba(0,0,0,0.05),0_8px_22px_-12px_rgba(0,0,0,0.16)] px-[20px] py-[24px] md:px-[28px] md:py-[28px] xl:px-[36px] xl:py-[32px]">
                {/* hlavicka: nazov + miesto + cena */}
                <div className="flex flex-wrap items-start justify-between gap-x-[24px] gap-y-[16px]">
                    <div className="min-w-0">
                        <h1 className="font-[700] text-[20px] leading-[1.15] tracking-[-0.01em] md:text-[23px] xl:text-[26px]">
                            {title}
                        </h1>
                        <div className="mt-[14px] flex flex-col gap-[5px]">
                            <div className="flex items-center gap-[6px]">
                                <LocationOutlinedIcon className="size-[15px] xl:size-[16px]" />
                                <p className="text-[14px] xl:text-[15px]">
                                    {listing.municipality?.name ?? ''}
                                    {listing.district?.name ? `, ${listing.district.name}` : ''}
                                </p>
                            </div>
                            <div className="flex items-center gap-[6px]">
                                <CategoryIcon className="size-[15px] xl:size-[16px]" />
                                <p className="text-[14px] xl:text-[15px]">
                                    {subtypeText ?? listing.propertyCategory}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col items-end shrink-0">
                        <div className="relative flex items-center justify-center w-fit">
                            <Img
                                src="/images/doodle.svg"
                                alt=""
                                className="absolute min-w-[130px] w-[155%] -mt-[8px] -ml-[20%] z-[1] pointer-events-none"
                            />
                            <span className="relative z-[2] text-[24px] font-[700] leading-[120%] xl:text-[26px]">
                                {formatListingPrice(listing)}
                            </span>
                        </div>
                        {pricePerM2 && <span className="mt-[4px] text-[12px] text-black/55">{pricePerM2}</span>}
                    </div>
                </div>

                <Separator className="my-[26px]" />

                {/* hlavne informacie */}
                <section>
                    <h2 className="font-[700] text-[18px] md:text-[19px] xl:text-[20px]">Hlavné informácie</h2>
                    <ul className="mt-[18px] grid grid-cols-1 gap-[12px] sm:grid-cols-2">
                        {mainInfo.map((item, idx) => {
                            const Icon = item.icon;
                            return (
                                <li key={idx} className="flex items-center gap-[8px] text-[14px] xl:text-[15px]">
                                    <Icon className="size-[16px] shrink-0 xl:size-[17px]" />
                                    <span>
                                        <b>{item.label}:</b> {item.value}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </section>

                {parking.length > 0 && (
                    <ListingDetailSection title="Parkovanie">
                        <ul className="grid grid-cols-1 gap-[12px]">
                            {parking.map((item, idx) => {
                                const Icon = item.icon;
                                return (
                                    <li key={idx} className="flex items-center gap-[8px] text-[14px] xl:text-[15px]">
                                        <Icon className="size-[16px] shrink-0 xl:size-[17px]" />
                                        <span className="font-[600]">{item.label}</span>
                                        {item.value !== 'Áno' && <span className="text-black/70">- {item.value}</span>}
                                    </li>
                                );
                            })}
                        </ul>
                    </ListingDetailSection>
                )}

                {outdoor.length > 0 && (
                    <ListingDetailSection title="Vonkajšie priestory">
                        <ul className="grid grid-cols-1 gap-[12px] sm:grid-cols-2">
                            {outdoor.map((item, idx) => {
                                const Icon = item.icon;
                                return (
                                    <li key={idx} className="flex items-center gap-[8px] text-[14px] xl:text-[15px]">
                                        <Icon className="size-[17px] shrink-0 xl:size-[18px]" />
                                        <span className="font-[600]">{item.label}</span>
                                        {item.value !== 'Áno' && <span className="text-black/70">- {item.value}</span>}
                                    </li>
                                );
                            })}
                        </ul>
                    </ListingDetailSection>
                )}

                {attributeSections.length > 0 && (
                    <>
                        <Separator className="my-[26px]" />
                        <Accordion type="multiple">
                            {attributeSections.map((section) => (
                                <AccordionItem key={section.title} value={section.title}>
                                    <AccordionTrigger className="font-[700] text-[17px] hover:no-underline py-[14px] xl:text-[18px]">
                                        {section.title}
                                    </AccordionTrigger>
                                    <AccordionContent className="h-auto pb-[18px]">
                                        <div className="flex flex-col gap-[18px]">
                                            {section.groups.map((group) => (
                                                <div key={group.title}>
                                                    <p className="mb-[8px] text-[13px] font-[600] xl:text-[14px]">{group.title}</p>
                                                    <div className="flex flex-wrap gap-[6px]">
                                                        {group.items.map((item, i) => (
                                                            <span
                                                                key={i}
                                                                className={cn(
                                                                    'inline-flex items-center rounded-[30px] border border-black/[0.06] bg-[#F2F1ED] px-[13px] py-[7px] text-[12px] xl:text-[13px]',
                                                                    'transition duration-300', EASE,
                                                                    'hover:bg-black hover:text-[#F3C300]',
                                                                    "before:mr-[7px] before:text-[10px] before:font-bold before:text-[#F3C300] before:content-['✓']",
                                                                )}
                                                            >
                                                                {item}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </>
                )}

                {listing.description && (
                    <ListingDetailSection title="Popis" withSeparator>
                        <ListingCollapse>
                            <div
                                className="text-[14px] leading-[165%] xl:text-[15px]"
                                dangerouslySetInnerHTML={{ __html: listing.description }}
                            />
                        </ListingCollapse>
                    </ListingDetailSection>
                )}

                <Separator className="my-[26px]" />

                <section>
                    <h2 className="mb-[12px] font-[700] text-[18px] md:text-[19px] xl:text-[20px]">Predáva</h2>
                    <ListingContactForm sellerContact={listing.sellerContact} />
                </section>
            </div>

            {/* podobne ponuky - karty z vysledkov, klik otvori dalsi detail bez opustenia mapy */}
            {similar.length > 0 && (
                <section className="mt-[28px]">
                    <div className="mb-[14px] flex items-end justify-between px-[4px]">
                        <h3 className="text-[17px] font-[700] xl:text-[18px]">Podobné ponuky</h3>
                        <button
                            type="button"
                            onClick={onClose}
                            className={cn(
                                'group flex items-center gap-[6px] text-[13px] font-[500] text-black/60',
                                'transition duration-300', EASE, 'hover:text-black',
                            )}
                        >
                            <ArrowLeft className="size-[13px] transition duration-300 group-hover:-translate-x-[2px]" strokeWidth={2} />
                            Všetky výsledky
                        </button>
                    </div>
                    <div className="grid gap-[16px] md:grid-cols-2 md:gap-[18px]">
                        {similar.map((l) => (
                            <SearchListingCard key={l.id} listing={l} onOpen={onOpen} />
                        ))}
                    </div>
                </section>
            )}
        </article>
    );
}

/** toolbar v detaile: spat | ulozit + zdielat - rovnake pilly ako maju vysledky */
export function DetailToolbar(props: {
    listing: Listing;
    count: number;
    saved: boolean;
    onToggleSaved: () => void;
    onClose: () => void;
}) {
    const countLabel =
        props.count === 1 ? '1 ponuku' : props.count >= 2 && props.count <= 4 ? `${props.count} ponuky` : `${props.count} ponúk`;
    const share = async () => {
        const url = window.location.href;
        const title = (props.listing as { slogan?: string }).slogan ?? 'Realico';
        try {
            if (navigator.share) await navigator.share({ title, url });
            else await navigator.clipboard.writeText(url);
        } catch {
            /* zrusene pouzivatelom */
        }
    };
    return (
        <div className="flex items-center justify-between gap-[12px]">
            <button
                type="button"
                onClick={props.onClose}
                className={cn(
                    'group flex min-w-0 items-center gap-[8px] text-[15px] font-[600] leading-none tracking-[-0.005em] xl:text-[16px]',
                    'transition duration-300', EASE,
                )}
            >
                <span
                    className={cn(
                        'flex size-[30px] shrink-0 items-center justify-center rounded-[15px] bg-white border border-black/10',
                        'transition duration-300', EASE,
                        'group-hover:bg-black group-hover:border-black group-hover:text-[#F3C300] group-hover:-translate-x-[2px]',
                    )}
                >
                    <ArrowLeft className="size-[14px]" strokeWidth={2.2} />
                </span>
                <span className="truncate">
                    Späť na {countLabel}
                    <span className="hidden font-[400] text-black/45 md:inline">
                        {' '}· {(props.listing as { slogan?: string }).slogan}
                    </span>
                </span>
            </button>

            <div className="flex shrink-0 items-center gap-[8px]">
                <button
                    type="button"
                    onClick={props.onToggleSaved}
                    className={cn(
                        'flex shrink-0 items-center gap-[6px] h-[36px] px-[13px] rounded-[18px]',
                        'border text-[12px] font-[500] leading-none',
                        'transition duration-300', EASE,
                        props.saved
                            ? 'border-[#F3C300] bg-[#F3C300] text-black'
                            : 'border-black/10 bg-white text-black hover:-translate-y-[1px] hover:shadow-[0_8px_20px_-10px_rgba(0,0,0,0.28)]',
                    )}
                >
                    <Heart className={cn('size-[13px]', props.saved && 'fill-black')} strokeWidth={2} />
                    <span className="max-md:hidden">{props.saved ? 'Uložené' : 'Uložiť ponuku'}</span>
                </button>
                <button
                    type="button"
                    onClick={share}
                    className={cn(
                        'flex shrink-0 items-center gap-[6px] h-[36px] px-[13px] rounded-[18px]',
                        'bg-white border border-black/10 text-[12px] font-[500] leading-none',
                        'transition duration-300', EASE,
                        'hover:-translate-y-[1px] hover:shadow-[0_8px_20px_-10px_rgba(0,0,0,0.28)]',
                    )}
                >
                    <Share2 className="size-[13px]" strokeWidth={2} />
                    <span className="max-md:hidden">Zdieľať</span>
                </button>
            </div>
        </div>
    );
}
